// Shared a11y behavior for dismissible UI — modals, dialogs, popovers,
// dropdowns. Implemented as a Svelte 5 attachment ({@attach}) so the
// element controls its own lifecycle without parents needing to wire
// keydown / outside-click / focus-restore plumbing each time.
//
// Two presets are exported:
//   modal    — full dialog treatment: Escape closes, Tab is trapped,
//              outside click closes, focus moves into the dialog on
//              mount and is restored on unmount.
//   popover  — lighter touch: Escape closes, outside click closes,
//              focus is NOT trapped (so Tab walks normally out of the
//              popover into surrounding page), focus is restored on
//              close.
//
// Both let callers override individual behaviors via options — pass
// `trapFocus: false` to `modal` for a dialog where Tab should escape,
// or `closeOnOutsideClick: false` to a popover that should require an
// explicit dismiss.

export interface DismissibleOptions {
  /** Fires when Escape is pressed or (when enabled) the user clicks outside. */
  onClose: () => void;
  /** Trap Tab navigation inside the element. Default: false. */
  trapFocus?: boolean;
  /** Close when the user clicks outside the element. Default: true. */
  closeOnOutsideClick?: boolean;
  /** Move focus into the element on mount. Default: true. */
  autoFocus?: boolean;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusables(node: HTMLElement): HTMLElement[] {
  return Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('aria-hidden'),
  );
}

/**
 * Svelte 5 attachment that wires a11y dismiss/focus behavior onto an
 * element. Usage:
 *
 *   <div role="dialog" {@attach dismissible({ onClose: () => (open = false) })}>
 *     …
 *   </div>
 *
 * The factory returns an attachment function (node => cleanup), so the
 * Svelte runtime calls it on mount and the cleanup on unmount.
 */
export function dismissible(opts: DismissibleOptions) {
  const trapFocus = opts.trapFocus ?? false;
  const closeOnOutsideClick = opts.closeOnOutsideClick ?? true;
  const autoFocus = opts.autoFocus ?? true;

  return (node: HTMLElement) => {
    const previouslyFocused =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;

    if (autoFocus) {
      // requestAnimationFrame so the element is in the DOM and focusable
      // children have settled (e.g. fresh inputs in just-revealed forms).
      requestAnimationFrame(() => {
        const first = focusables(node)[0] ?? node;
        first?.focus?.();
      });
    }

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        opts.onClose();
        return;
      }
      if (trapFocus && e.key === 'Tab') {
        const f = focusables(node);
        if (f.length === 0) {
          e.preventDefault();
          return;
        }
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    function onPointerDown(e: PointerEvent): void {
      if (!closeOnOutsideClick) return;
      if (!(e.target instanceof Node)) return;
      if (!node.contains(e.target)) {
        opts.onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    // pointerdown beats click + handles mouse / touch / pen uniformly.
    document.addEventListener('pointerdown', onPointerDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('pointerdown', onPointerDown);
      // Restore focus to whatever had it before the dismissible opened.
      // Skip if focus has wandered elsewhere intentionally (the previous
      // element may be unmounted by now).
      if (
        previouslyFocused &&
        previouslyFocused.isConnected &&
        document.activeElement !== previouslyFocused
      ) {
        previouslyFocused.focus?.();
      }
    };
  };
}

/** Shorthand for `dismissible({ trapFocus: true, ... })` — for dialogs. */
export function modal(opts: DismissibleOptions) {
  return dismissible({ trapFocus: true, ...opts });
}

/** Shorthand for `dismissible({ trapFocus: false, ... })` — for popovers/dropdowns. */
export function popover(opts: DismissibleOptions) {
  return dismissible({ trapFocus: false, ...opts });
}
